import { toArray } from 'rise-ui/utils/array.js';
import { isSet } from 'rise-ui/utils/nullish.js';
import { parseDrawOptions } from 'rise-ui/utils/canvas/graph-draw.js';
import { drawLineSegment } from 'rise-ui/utils/canvas.js';
import { identity } from 'rise-ui/utils/function.js';

/**
 * @name DrawGraphLineOptionsArg
 * @property {function} [transform] Function to transform each vertex
 * @property {string} [x] Key in transformed vertex for x value (default: 'x')
 * @property {string} [y] Key in transformed vertex for y value(default: 'y')
 * @property {function} [drawSegment]
 * @property {function} [drawNode]
 * @property {function} [drawNull]
 */
/**
 * Iterates vertices and draws lines/nodes/nulls by delegate
 * @param {[]} vertices
 * @param {DrawGraphLineOptionsArg} options
 */
export function drawGraphLine(vertices, options) {
  vertices = toArray(vertices);
  const { drawSegment, drawNode, drawNull } = options;
  if (!vertices.length || !(drawSegment || drawNode || drawNull)) {
    return;
  }
  const { transform = identity, x = 'x', y = 'y' } = options;
  const endIndex = vertices.length;
  let lastX, lastY, nextX, nextY, isNextSet;
  let isLastLastSet = false, isLastSet = false;
  for (let i = 0; i <= endIndex;
    ++i, lastX = nextX, lastY = nextY, isLastLastSet = isLastSet, isLastSet = isNextSet
  ) {
    const vertex = vertices[i];
    isNextSet = i < endIndex;
    if (isNextSet) {
      ({ [x]: nextX, [y]: nextY } = transform(vertex, i));
      isNextSet = isSet(nextX) && isSet(nextY);
      if (!isNextSet) {
        drawNull?.(vertex, i - 1, vertices);
        if (isLastSet && !isLastLastSet) {
          drawNode?.(lastX, lastY, vertices[i - 1], i - 1, vertices);
        }
        continue;
      }
    }
    if (!i || !drawSegment) {
      continue;
    }
    if (isLastSet) {
      if (!isLastLastSet) {
        drawSegment(lastX, lastY, true, vertices[i - 1], i - 1, vertices);
      }
      drawSegment(nextX, nextY, false, vertex, i, vertices);
    }
  }
}

/**
 * @name DrawGraphLinesNodeArg
 * @param {number} x
 * @param {number} y
 * @param {{}} [line] Per line options
 * @param {string} [line.strokeStyle]
 */
/**
 * @name DrawGraphLinesOptionsArg
 * @extends DrawOptions
 * @property {string} [strokeStyle] (default: '#000')
 */
/**
 * Draw line graph
 * @param {CanvasRenderingContext2D} context
 * @param {DrawGraphLinesNodeArg[]} vertices
 * @param {DrawGraphLinesOptionsArg} options
 */
export function drawGraphLines(context, vertices, options) {
  options = parseDrawOptions(options);
  const px = options.paddingLeft;
  const py = options.paddingTop;
  context.strokeStyle = options.strokeStyle || '#000';
  context.beginPath();
  drawGraphLine(vertices, {
    transform: options.transform,
    drawSegment: (x, y, pathStart) =>
      drawLineSegment(context, px + x, py + y, pathStart),
  });
  context.stroke();
}
